/**
 * CT-269: the ClickTerm promo destination is an attribution contract, not a normal link.
 *
 * The query string is assembled by literal concatenation on purpose. `URL`/`URLSearchParams`
 * round-trips would re-encode and reorder the params, and attribution is lost silently
 * downstream when that happens; no error surfaces anywhere.
 */
export const CLICKTERM_PROMO_ORIGIN = 'https://clickterm.com/';
export const CLICKTERM_PROMO_SOURCE = 'telq-app';
export const CLICKTERM_PROMO_MEDIUM = 'referral';
export const CLICKTERM_PROMO_CAMPAIGN = 'clickterm-promo';

/**
 * Per-placement campaign suffixes. utm_campaign is the only dimension that varies:
 * utm_source/utm_medium stay fixed, and utm_content is not read downstream.
 */
export type ClicktermPromoPlacement = 'dashboard' | 'results' | 'news';

const MAX_PARAM_LENGTH = 100;

/**
 * Downstream keeps only [A-Za-z0-9_ .:/@+-] and strips the rest. This pattern is the
 * subset of that charset which also survives a URL verbatim, so no escaping is ever needed.
 */
const UNESCAPED_SAFE_VALUE = /^[A-Za-z0-9_.+-]+$/;

export function isValidClicktermPromoParam(value: string): boolean {
  return value.length < MAX_PARAM_LENGTH && UNESCAPED_SAFE_VALUE.test(value);
}

export function buildClicktermPromoCampaign(placement?: ClicktermPromoPlacement | null): string {
  if (!placement) {
    return CLICKTERM_PROMO_CAMPAIGN;
  }

  const campaign = `${CLICKTERM_PROMO_CAMPAIGN}-${placement}`;

  // Falling back to the base campaign keeps the click attributable; an invalid value
  // would be stripped downstream and the signup would land with no campaign at all.
  return isValidClicktermPromoParam(campaign) ? campaign : CLICKTERM_PROMO_CAMPAIGN;
}

export function buildClicktermPromoUrl(placement?: ClicktermPromoPlacement | null): string {
  const campaign = buildClicktermPromoCampaign(placement);

  return `${CLICKTERM_PROMO_ORIGIN}?utm_source=${CLICKTERM_PROMO_SOURCE}&utm_medium=${CLICKTERM_PROMO_MEDIUM}&utm_campaign=${campaign}`;
}
